/**
 * Frame-data predicates (owner: movement team), read by the controller, the contact module, the AI and render.
 * Every window is INCLUSIVE and counted in `stateFrame` (1 = first frame in the state), per tuning.FRAMES.
 */
import { FRAMES, MOVE } from '@hf/shared';
import { PlayerState } from '../enums';
import type { PlayerSim } from '../state';

const D = FRAMES.dive;
const L = FRAMES.leapGrab;
const SA = FRAMES.stiffArm;
const SP = FRAMES.spin;
const T = FRAMES.truck;
const TR = FRAMES.throwRelease;

/** Total frames of a dive (startup + active + recovery). */
export const DIVE_TOTAL = D.startup + D.active + D.recovery;
export const STIFF_ARM_TOTAL = SA.startup + SA.active + SA.recovery;
export const THROW_RELEASE_TOTAL = TR.startup + TR.active + TR.recovery;
export const TRUCK_BURST_TOTAL = T.burst + T.recovery;
export const DASH_TOTAL = MOVE.dashFrames + MOVE.dashRecoveryFrames;
export const BACKSTEP_TOTAL = FRAMES.stutter.backstep + FRAMES.stutter.recovery;

const inWindow = (f: number, startup: number, active: number): boolean =>
  f > startup && f <= startup + active;

/** DIVE active (low grab hitbox live). */
export const diveActive = (p: PlayerSim): boolean =>
  p.state === PlayerState.DIVE && inWindow(p.stateFrame, D.startup, D.active);
/** DIVE still in its (fake-able) startup. */
export const diveStartup = (p: PlayerSim): boolean =>
  p.state === PlayerState.DIVE && p.stateFrame <= D.startup;
/** LEAP_GRAB active (high grab hitbox live). */
export const leapActive = (p: PlayerSim): boolean =>
  p.state === PlayerState.LEAP_GRAB && inWindow(p.stateFrame, L.startup, L.active);
/** STIFF_ARM active frames (Fist out: beats a contact grab). */
export const stiffArmActive = (p: PlayerSim): boolean =>
  p.state === PlayerState.STIFF_ARM && inWindow(p.stateFrame, SA.startup, SA.active);
/** TRUCK_BURST burst frames (low shoulder charge). */
export const truckBursting = (p: PlayerSim): boolean =>
  p.state === PlayerState.TRUCK_BURST && p.stateFrame <= T.burst;
/** Spin i-frames running. */
export const spinInvulnerable = (p: PlayerSim): boolean => p.state === PlayerState.SPIN && p.iFrames > 0;
/** Startup + active of a spin (the head is turning; i-frames from startup + 1). */
export const spinMoving = (p: PlayerSim): boolean =>
  p.state === PlayerState.SPIN && p.stateFrame <= SP.startup + SP.active;
/** Truck charge fraction 0..1 (minCharge → 0, maxCharge → 1). */
export const truckChargeFrac = (p: PlayerSim): number => {
  const k = (p.truckCharge - T.minCharge) / (T.maxCharge - T.minCharge);
  return k < 0 ? 0 : k > 1 ? 1 : k;
};
/** Throw charge fraction 0..1 (FRAMES.throwCharge). */
export const throwChargeFrac = (p: PlayerSim): number => {
  const c = FRAMES.throwCharge;
  const k = (p.throwCharge - c.minCharge) / (c.maxCharge - c.minCharge);
  return k < 0 ? 0 : k > 1 ? 1 : k;
};
/** The truck hurtbox is squashed (TRUCK_CHARGE or TRUCK_BURST, head on the ground). */
export const truckSquashed = (p: PlayerSim): boolean =>
  p.grounded && (p.state === PlayerState.TRUCK_CHARGE || truckBursting(p));
